/**
 * Insere `text` na posicao do cursor de um input/textarea, mantendo undo
 * historico (via nativeInputValueSetter + evento 'input' que o React entende).
 *
 * Se `el` for null OU nao for input/textarea, faz fallback: appenda o texto ao
 * value atual (mantem consistencia mesmo em cenarios de perda de foco).
 *
 * O React usa "controlled inputs" — chamar `el.value = X` diretamente NAO
 * dispara `onChange`. Precisamos ir via `HTMLInputElement.prototype`
 * (nativeInputValueSetter) e disparar um `Event('input', bubbles:true)` pro
 * React registrar a mudanca no fiber tree e chamar o onChange do consumidor.
 * Padrao React 17+ que sobrevive a strict-mode.
 */
export function insertAtCursor(el: HTMLInputElement | HTMLTextAreaElement | null, text: string): void {
    if (!text) return;

    if (!el) return;

    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    const before = el.value.substring(0, start);
    const after = el.value.substring(end);
    const nextValue = `${before}${text}${after}`;

    // React controla o input — precisamos usar o setter nativo pra bypassar
    // o interceptor do React e depois disparar 'input' pra sincronizar state.
    const proto = el instanceof HTMLTextAreaElement
        ? window.HTMLTextAreaElement.prototype
        : window.HTMLInputElement.prototype;
    const nativeSetter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
    if (nativeSetter) {
        nativeSetter.call(el, nextValue);
    } else {
        el.value = nextValue;
    }

    el.dispatchEvent(new Event('input', { bubbles: true }));

    // Reposiciona cursor apos o texto inserido pra fluidez de digitacao continua.
    const newPos = start + text.length;
    requestAnimationFrame(() => {
        try {
            el.setSelectionRange(newPos, newPos);
            el.focus();
        } catch { /* elementos ja unmounted — silent */ }
    });
}
